import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fromJS, is as isCompareImmutable } from "immutable";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@headlessui/react";
import {
  ArrowLeft,
  Boxes,
  Building,
  Check,
  Dot,
  Eye,
  EyeOff,
  GripVertical,
  Settings,
  SquareKanban,
  Users,
} from "lucide-react";
import { ContentTypes } from "../constants/action-types";
import TaskAction from "../actions/TaskAction";
import LayoutAction from "../actions/LayoutAction";
import { Button, Modal } from "../components/ui";
import SaleForm from "../main/form/SaleForm";
import ContactForm from "../main/form/ContactForm";
import CompanyForm from "../main/form/CompanyForm";
import ProductForm from "../main/form/ProductForm";
import { createItem, saveFormSetting, search } from "../api/ProjectApi";
import { checkValidPhoneNumber } from "../components/ui/PhoneInputFieldDebounce";
import { useQuery } from "../AppLayout";
import ConfirmDialog from "./ConfirmDialog";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import Container from "../board/dnd/Container";
import Draggable from "../board/dnd/Draggable";
import ProjectAction from "../actions/ProjectAction";

export const checkExistEmail = async (projectId, email, type = "CONTACT") => {
  if (!email) return false;
  const data = await search({
    projectId: projectId,
    pageSize: 1,
    types: [type],
    filters: {
      email: {
        operator: "is",
        values: [email.trim()],
      },
    },
  });
  const items = fromJS(data?.pageResult?.content || []);
  return items.size > 0;
};

export const checkExistProductCode = async (projectId, productCode) => {
  if (!productCode) return false;
  const data = await search({
    projectId: projectId,
    pageSize: 1,
    types: ["PRODUCT"],
    filters: {
      productCode: {
        operator: "is",
        values: [productCode.trim()],
      },
    },
  });
  const items = fromJS(data?.pageResult?.content || []);
  return items.size > 0;
};

export const checkExistDomain = async (projectId, domain) => {
  if (!domain) return false;
  const data = await search({
    projectId: projectId,
    pageSize: 1,
    types: ["COMPANY"],
    filters: {
      domains: {
        operator: "is",
        values: [domain],
      },
    },
  });
  const items = fromJS(data?.pageResult?.content || []);
  return items.size > 0;
};

export const checkExistWebsite = async (projectId, website) => {
  if (!website) return false;

  let domain = website.trim();
  try {
    const url = new URL(domain.startsWith("http") ? domain : `https://${domain}`);
    domain = url.hostname.replace(/^www\./, "");
  } catch {
    // fallback to original if parsing fails
  }

  return checkExistDomain(projectId, domain);
};

const ALL_FIELDS = {
  [ContentTypes.SALE]: [
    "name",
    "bucketId",
    "assignees",
    "labels",
    "contact",
    "account",
    "issue",
    "estimatedRevenue",
    "estimatedProbability",
    "status",
    "finish",
    "stage",
    "source",
  ],
  [ContentTypes.COMPANY]: [
    "name",
    "description",
    "domains",
    "email",
    "phone",
    "industry",
    "contact",
    "assignees",
    "labels",
    "bucketId",
    "addressGroup", // All address
    "website",
    "employeeRange",
    "estimatedArr",
    "linkedIn",
    "instagram",
    "twitter",
    "facebook",
  ],
  [ContentTypes.CONTACT]: [
    "name",
    "description",
    "email",
    "phone",
    "jobTitle",
    "salutation",
    "birthday",
    "account",
    "assignees",
    "labels",
    "bucketId",
    "addressGroup", // All address
    "linkedIn",
    "instagram",
    "twitter",
    "facebook",
  ],
  [ContentTypes.PRODUCT]: [
    "name",
    "productCode",
    "unit",
    "price",
    "unitPrice",
    "tax",
    "description",
    "assignees",
    "labels",
    "bucketId",
  ],
};

const DEFAULT_FIELDS = {
  [ContentTypes.SALE]: [
    "name",
    "issue",
    "stage",
    "contact",
    "account",
    "estimatedRevenue",
    "finish",
    "assignees",
  ],
  [ContentTypes.COMPANY]: [
    "name",
    "website",
    "industry",
    "employeeRange",
    "estimatedArr",
    "addressGroup",
  ],
  [ContentTypes.CONTACT]: ["name", "jobTitle", "email", "phone", "account"],
  [ContentTypes.PRODUCT]: ["name", "productCode", "unit", "price", "unitPrice", "tax"],
};

const ItemDialog = ({ show, contentType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentParams = useQuery();
  const projectView = currentParams.get("view") || "GRID";
  const projectContentType = currentParams.get("type") || "SALE";
  const queryClient = useQueryClient();

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const project = useSelector((state: any) => state.projectReducer.getIn(["project"]));
  const projectId = project.get("id");
  const pagination = useSelector((state: any) => state.taskReducer.getIn(["pagination"]));
  const taskFilters = useSelector((state: any) => state.taskReducer.getIn(["filters"]));
  const sortFields = useSelector((state: any) => state.taskReducer.getIn(["sorts"]));
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["showTaskDialog", contentType, "task"])) ||
    fromJS({});
  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const formSettingByType =
    useSelector((state: any) =>
      state.projectReducer.getIn(["project", "formSetting", contentType])
    ) || fromJS({});

  const itemId = task.get("id");
  const isEditMode = (itemId || "") !== "";

  const [isSettings, setIsSettings] = useState(false);
  const [newTaskData, setNewTaskData] = useState<any>(fromJS({}));
  const [createMore, setCreateMore] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
  const [visibleFields, setVisibleFields] = useState<any[]>([]);
  const [invisibleFields, setInvisibleFields] = useState<any[]>([]);

  useEffect(() => {
    if (show && !isEditMode) {
      setNewTaskData(task.get("data") || fromJS({}));
    }
  }, [show, isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleFieldIds = useMemo(() => {
    let fieldIds = DEFAULT_FIELDS[contentType];
    const visibleFieldIdsData = formSettingByType.get("visibleFieldIds")?.toJS() || [];
    if (visibleFieldIdsData.length > 0) {
      fieldIds = visibleFieldIdsData;
    }
    return fieldIds.filter((fieldId) => (isJira ? true : fieldId !== "issue"));
  }, [isJira, contentType, formSettingByType]);

  useEffect(() => {
    if (isSettings) {
      const allFields = fromJS([
        ...fields.toJS(),
        {
          id: "addressGroup",
          type: "group",
          label: "Address",
          formType: "COMPANY",
          isEditable: true,
          isSearchable: true,
          isEnrichable: true,
          projectId: "",
          tenantId: "",
          isCustomField: false,
        },
        {
          id: "addressGroup",
          type: "group",
          label: "Address",
          formType: "CONTACT",
          isEditable: true,
          isSearchable: true,
          isEnrichable: true,
          projectId: "",
          tenantId: "",
          isCustomField: false,
        },
      ]).filter((field) => (isJira ? true : field.get("id") !== "issue"));
      const customFields = allFields.filter(
        (field) => field.get("isCustomField") && field.get("formType") == contentType
      );
      const customFieldIds = customFields.map((field) => field.get("id")).toJS();
      const allFieldIds = [...ALL_FIELDS[contentType], ...customFieldIds];
      const visibleFieldsData = visibleFieldIds
        .map((visibleField) =>
          allFields
            .find(
              (field) => field.get("formType") == contentType && visibleField === field.get("id")
            )
            ?.toJS()
        )
        .filter(Boolean);
      const invisibleFieldsData = allFields
        .filter(
          (field) =>
            field.get("formType") == contentType &&
            allFieldIds.includes(field.get("id")) &&
            !visibleFieldIds.includes(field.get("id"))
        )
        .toJS();
      setVisibleFields(visibleFieldsData);
      setInvisibleFields(invisibleFieldsData);
    }
  }, [isSettings, fields, isJira, contentType, visibleFieldIds]);

  const renderTitle = () => {
    if (isEditMode) {
      switch (contentType) {
        case ContentTypes.SALE:
          return t("Deal");
        case ContentTypes.CONTACT:
          return t("Contact");
        case ContentTypes.COMPANY:
          return t("Account");
        case ContentTypes.PRODUCT:
          return t("Product");
        default:
          return t("Item");
      }
    } else {
      switch (contentType) {
        case ContentTypes.SALE:
          return t("Add New Deal");
        case ContentTypes.CONTACT:
          return t("Add New Contact");
        case ContentTypes.COMPANY:
          return t("Add New Account");
        case ContentTypes.PRODUCT:
          return t("Add New Product");
        default:
          return t("Add New Item");
      }
    }
  };

  const getTaskFieldValue = (fieldId: string) => {
    return task.getIn(["data", fieldId, "value"]);
  };

  const validateForm = async () => {
    const errors: Record<string, string> = {};

    if (!getTaskFieldValue("name")?.trim()) {
      if (contentType === ContentTypes.SALE) {
        errors.name = t("Deal name is required");
      } else if (contentType === ContentTypes.CONTACT) {
        errors.name = t("Contact name is required");
      } else if (contentType === ContentTypes.COMPANY) {
        errors.name = t("Account name is required");
      } else if (contentType === ContentTypes.PRODUCT) {
        errors.name = t("Product name is required");
      }
    }

    const productCode = getTaskFieldValue("productCode")?.trim();
    if (!productCode) {
      if (contentType === ContentTypes.PRODUCT) {
        errors.productCode = t("Product code is required");
      }
    } else {
      const isExistProductCode = await checkExistProductCode(projectId, productCode);
      if (isExistProductCode) {
        errors.productCode = t("The product code already exists");
      }
    }

    if (contentType === ContentTypes.PRODUCT) {
      const price = getTaskFieldValue("price");
      if (price === undefined || price === null || price === "") {
        errors.price = t("Price is required");
      }

      const unitPrice = getTaskFieldValue("unitPrice");
      if (unitPrice === undefined || unitPrice === null || unitPrice === "") {
        errors.unitPrice = t("Unit price is required");
      }
    }

    const email = getTaskFieldValue("email")?.trim();
    if (!email) {
      if (contentType === ContentTypes.CONTACT) {
        errors.email = t("Email is required");
      }
    } else {
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        errors.email = t("Invalid email format");
      } else {
        const isExistEmail = await checkExistEmail(projectId, email, contentType);
        if (isExistEmail) {
          errors.email = t("The email already exists");
        }
      }
    }
    const phone = getTaskFieldValue("phone")?.trim();
    if (phone) {
      if (!checkValidPhoneNumber(phone)) {
        errors.phone = t("Invalid phone number");
      }
    }

    const website = getTaskFieldValue("website")?.trim();
    if (website && contentType === ContentTypes.COMPANY) {
      const urlPattern = new RegExp(
        "^(https?:\\/\\/)?" + // validate protocol
          "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
          "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
          "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
          "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
          "(\\#[-a-z\\d_]*)?$",
        "i"
      );
      if (!urlPattern.test(website)) {
        errors.website = t("Invalid website format");
      } else {
        const isExistWebsite = await checkExistWebsite(projectId, website);
        if (isExistWebsite) {
          errors.website = t("The website already exists");
        }
      }
    }

    if (contentType === ContentTypes.COMPANY) {
      const domainsField = getTaskFieldValue("domains");
      if (domainsField) {
        const domainsList = domainsField.toJS
          ? domainsField.toJS()
          : Array.isArray(domainsField)
            ? domainsField
            : [];
        for (const d of domainsList) {
          const isExistDomain = await checkExistDomain(projectId, d);
          if (isExistDomain) {
            errors.domains = t(`The domain ${d} already exists`);
            break;
          }
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      const firstKey = Object.keys(errors)[0];
      document.getElementsByName(firstKey)[0]?.focus();
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDataChange = (fieldId, fieldValue) => {
    dispatch(TaskAction.updateTaskDialog(contentType, fieldId, fieldValue));
  };

  const reloadItems = () => {
    const page = pagination.getIn(["number"]);
    const pageSize = pagination.getIn(["size"]);
    const filters = taskFilters.toJS();
    const sorts = sortFields.toJS();
    dispatch(
      TaskAction.searchItems({
        projectId,
        page,
        pageSize,
        filters,
        sorts,
        types: [projectContentType],
        view: projectView,
      })
    );
  };

  const handleCreateOrUpdate = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }
    if (isEditMode) {
      // TODO
    } else {
      let title = t("Item");
      let messageSuccess = t("Add new item successfully!");
      let messageError = t("Add new item failed!");
      switch (contentType) {
        case ContentTypes.SALE:
          title = t("Deal");
          messageSuccess = t("Add new deal successfully!");
          messageError = t("Add new deal failed!");
          break;
        case ContentTypes.CONTACT:
          title = t("Contact");
          messageSuccess = t("Add new contact successfully!");
          messageError = t("Add new contact failed!");
          break;
        case ContentTypes.COMPANY:
          title = t("Account");
          messageSuccess = t("Add new account successfully!");
          messageError = t("Add new account failed!");
          break;
        case ContentTypes.PRODUCT:
          title = t("Product");
          messageSuccess = t("Add new product successfully!");
          messageError = t("Add new product failed!");
          break;
      }
      setIsSaving(true);
      const newItemData = {
        ...task.toJS(),
        data: {
          ...task.get("data").toJS(),
        },
      };
      createItem(newItemData)
        .then(() => {
          if (!createMore) {
            handleClose();
          } else {
            setValidationErrors({});
            dispatch(TaskAction.resetTaskDialog(contentType, newTaskData.toJS()));
          }
          dispatch(
            LayoutAction.setNotification({
              title: title,
              type: "success",
              message: messageSuccess,
            })
          );
          reloadItems();
          // Reload list item
          queryClient.invalidateQueries({ queryKey: [contentType, projectId, ""] });
          setIsSaving(false);
        })
        .catch(() => {
          if (!createMore) {
            handleClose();
          } else {
            setValidationErrors({});
            dispatch(TaskAction.resetTaskDialog(contentType, newTaskData.toJS()));
          }
          dispatch(
            LayoutAction.setNotification({
              title: title,
              type: "error",
              message: messageError,
            })
          );
          setIsSaving(false);
        });
    }
  };

  const handleSaveSettings = async () => {
    const visibleFieldIdsData = visibleFields.map((field) => field.id);
    await saveFormSetting(projectId, contentType, { visibleFieldIds: visibleFieldIdsData });
    dispatch(ProjectAction.getFormSetting(projectId, contentType));
    dispatch(
      LayoutAction.setNotification({
        title: t("Configure Fields"),
        type: "success",
        message: t("Configure fields successfully"),
      })
    );
  };

  const handleClose = () => {
    setIsSettings(false);
    setVisibleFields([]);
    setInvisibleFields([]);
    setValidationErrors({});
    setCreateMore(false);
    dispatch(TaskAction.hideTaskDialog(contentType));
  };

  const handleCloseModal = () => {
    const cleanMap = (map) =>
      map.filter(
        (field) =>
          field.get("value") !== "" &&
          field.get("value") !== null &&
          field.get("value") !== undefined
      );
    const taskData = task.get("data") || fromJS({});
    const isDirty = !isCompareImmutable(cleanMap(taskData), cleanMap(newTaskData));
    if (isDirty) {
      setShowConfirmCloseModal(true);
    } else {
      handleClose();
    }
  };

  const renderContentTypeIcon = () => {
    if (contentType === ContentTypes.SALE) {
      return <SquareKanban className="w-7 h-7 text-gray-600" />;
    } else if (contentType === ContentTypes.CONTACT) {
      return <Users className="w-7 h-7 text-gray-600" />;
    } else if (contentType === ContentTypes.COMPANY) {
      return <Building className="w-7 h-7 text-gray-600" />;
    } else if (contentType === ContentTypes.PRODUCT) {
      return <Boxes className="w-7 h-7 text-gray-600" />;
    }
    return null;
  };

  const renderForm = () => {
    if (contentType === ContentTypes.SALE) {
      return (
        <SaleForm
          projectId={projectId}
          visibleFieldIds={visibleFieldIds}
          isEditMode={isEditMode}
          taskData={task.get("data") || fromJS({})}
          onDataChange={handleDataChange}
          validationErrors={validationErrors}
        />
      );
    } else if (contentType === ContentTypes.CONTACT) {
      return (
        <ContactForm
          projectId={projectId}
          visibleFieldIds={visibleFieldIds}
          isEditMode={isEditMode}
          taskData={task.get("data") || fromJS({})}
          onDataChange={handleDataChange}
          validationErrors={validationErrors}
        />
      );
    } else if (contentType === ContentTypes.COMPANY) {
      return (
        <CompanyForm
          projectId={projectId}
          visibleFieldIds={visibleFieldIds}
          isEditMode={isEditMode}
          taskData={task.get("data") || fromJS({})}
          onDataChange={handleDataChange}
          validationErrors={validationErrors}
        />
      );
    } else if (contentType === ContentTypes.PRODUCT) {
      return (
        <ProductForm
          projectId={projectId}
          visibleFieldIds={visibleFieldIds}
          isEditMode={isEditMode}
          taskData={task.get("data") || fromJS({})}
          onDataChange={handleDataChange}
          validationErrors={validationErrors}
        />
      );
    }
    return null;
  };

  const renderSettings = () => {
    const applyDrag = (arr, { removedIndex, addedIndex, payload }) => {
      const result = [...arr];
      if (removedIndex !== null) {
        result.splice(removedIndex, 1);
      }
      if (addedIndex !== null) {
        result.splice(addedIndex, 0, payload);
      }
      return result;
    };
    return (
      <div className="p-4">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5" />
          <div className="text-base font-medium">{t("Visible fields")}</div>
        </div>
        <div className="px-4 py-2">
          <Container
            groupName="fields"
            getChildPayload={(i) => visibleFields[i]}
            onDrop={(e) => setVisibleFields((prev) => applyDrag(prev, e))}
            dragHandleSelector={".drag-handle"}
            nonDragAreaSelector={".no-drag"}
          >
            {visibleFields.map((item, index) => {
              let isRequiredField = false;
              if (contentType === ContentTypes.SALE) {
                if (item.id === "name") {
                  isRequiredField = true;
                }
              } else if (contentType === ContentTypes.COMPANY) {
                if (item.id === "name") {
                  isRequiredField = true;
                }
              } else if (contentType === ContentTypes.CONTACT) {
                if (item.id === "name" || item.id === "email") {
                  isRequiredField = true;
                }
              } else if (contentType === ContentTypes.PRODUCT) {
                if (item.id === "name" || item.id === "productCode") {
                  isRequiredField = true;
                }
              }
              return (
                <Draggable key={`${item.id}_${index}`}>
                  <div
                    className={`w-full p-2 flex items-center gap-2 rounded-lg hover:bg-gray-100 ${isRequiredField ? "no-drag" : "drag-handle"}`}
                  >
                    <div className={isRequiredField ? "" : "cursor-move"}>
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="cursor-default flex items-center gap-2 text-sm">
                      <span>{item.label}</span>
                      {isRequiredField && (
                        <>
                          <Dot className="w-4 h-4" />
                          <span className="text-red-600">{t("required")}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Draggable>
              );
            })}
          </Container>
        </div>
        <div className="flex items-center gap-2">
          <EyeOff className="w-5 h-5" />
          <div className="text-base font-medium">{t("Hidden fields")}</div>
        </div>
        <div className="px-4 py-2">
          <Container
            groupName="fields"
            getChildPayload={(i) => invisibleFields[i]}
            onDrop={(e) => {
              // Disable reoder invisible fields
              // if (e.removedIndex !== null && e.addedIndex !== null) return;
              setInvisibleFields((prev) => applyDrag(prev, e));
            }}
            dragHandleSelector={".drag-handle"}
            nonDragAreaSelector={".no-drag"}
          >
            {invisibleFields.map((item, index) => {
              return (
                <Draggable key={`${item.id}_${index}`}>
                  <div className="w-full p-2 flex items-center gap-2 rounded-lg hover:bg-gray-100 drag-handle">
                    <div className="cursor-move">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="cursor-default text-sm">
                      <span>{item.label}</span>
                    </div>
                  </div>
                </Draggable>
              );
            })}
          </Container>
        </div>
      </div>
    );
  };

  const modalContent = <div className="p-4">{renderForm()}</div>;

  const modalFooter = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        {!isEditMode && (
          <div className="flex items-center space-x-2">
            <Switch
              checked={createMore}
              onChange={setCreateMore}
              className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 data-[checked]:bg-blue-600"
            >
              <Check
                className={`absolute z-1 top-1 right-1 h-3 w-3 ${
                  createMore ? "text-blue-600" : "text-gray-600"
                }`}
              />
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
              />
            </Switch>
            <span className="text-gray-500 text-sm font-semibold">{t("Create more")}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-end gap-3">
        <Button size="sm" variant="secondary" onClick={handleCloseModal}>
          {t("Cancel")}
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={isSaving}
          onClick={handleCreateOrUpdate}
          dataTestId="item-dialog-submit-button"
        >
          {isSaving ? t("Saving...") : isEditMode ? t("Update") : t("Add")}
        </Button>
      </div>
    </div>
  );

  if (!show) return null;

  return (
    <>
      <Modal
        isOpen={show}
        onClose={handleCloseModal}
        title={<>{isSettings ? t("Configure Fields") : renderTitle()}</>}
        leftExtend={
          <>
            {isSettings ? (
              <button
                className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setIsSettings(false)}
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
            ) : (
              renderContentTypeIcon()
            )}
          </>
        }
        rightExtend={
          <>
            {isSettings ? (
              <Button size="sm" variant="primary" onClick={handleSaveSettings}>
                {t("Save")}
              </Button>
            ) : (
              <button
                className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                onClick={() => setIsSettings(true)}
              >
                <Settings className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </>
        }
        className="w-2xl max-h-150"
        footer={isSettings ? undefined : modalFooter}
      >
        {isSettings ? renderSettings() : modalContent}
      </Modal>
      <ConfirmDialog
        visible={showConfirmCloseModal}
        title={t("Are you sure?")}
        description={t("This will discard your current item.")}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={() => {
          handleClose();
          setShowConfirmCloseModal(false);
        }}
        submitBtnLabel={t("Discard")}
        dangerBtn={true}
        onClose={() => setShowConfirmCloseModal(false)}
      />
    </>
  );
};

export default ItemDialog;
